import { ComponentProps, RefObject, useState } from 'react';
import { toast } from 'react-toastify';
import { useDownloadCgv, useDownloadDevis } from '../../../../../hooks/Marketing';
import type BatPreviewDialog from '../BatPreviewDialog';
import type { CreationStepHandle } from '../CampaignCreationStep';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import type { TargetingFormData } from '../CampaignTargetingStep';
import { readSmsMessage } from '../creationPayload';

type BatPreviewProps = ComponentProps<typeof BatPreviewDialog>;

interface BatPreviewState {
  open: boolean;
  content: BatPreviewProps['content'];
  channel: NonNullable<BatPreviewProps['channel']>;
}

interface ValidationActionsOptions {
  definition: DefinitionFormData;
  targeting: TargetingFormData;
  creationPayloadRef: RefObject<Record<string, unknown> | null>;
  creationStepRef: RefObject<CreationStepHandle | null>;
}

export const useValidationActions = ({ definition, targeting, creationPayloadRef, creationStepRef }: ValidationActionsOptions) => {
  const { downloadDevis, loading: downloadingDevis } = useDownloadDevis();
  const { downloadCgv, loading: downloadingCgv } = useDownloadCgv();
  const [previewingBat, setPreviewingBat] = useState(false);
  const [batPreview, setBatPreview] = useState<BatPreviewState>({ open: false, content: null, channel: 'EMAIL' });

  const previewBat = async () => {
    setPreviewingBat(true);
    try {
      if (definition.channel === 'sms') {
        const message = readSmsMessage(creationPayloadRef.current);
        if (!message.trim()) {
          toast.info("Rédigez d'abord le message SMS à l'étape Création.");
          return;
        }
        setBatPreview({ open: true, channel: 'SMS', content: { html: message, subject: '' } });
        return;
      }
      const content = definition.channel === 'email' ? await creationStepRef.current?.getEmailContent() : null;
      if (!content) {
        toast.info('Aperçu du BAT non disponible pour ce canal.');
        return;
      }
      setBatPreview({ open: true, channel: 'EMAIL', content });
    } finally {
      setPreviewingBat(false);
    }
  };

  const handleDownloadDevis = async () => {
    const ok = await downloadDevis({
      title: definition.title,
      channel: definition.channel,
      sendDate: definition.sendDate,
      targetingSummary: targeting.queryDescription ? `Requête de ciblage : ${targeting.queryDescription}` : 'Aucun ciblage défini.',
      nbRecipients: targeting.nbRecipients,
    });
    if (!ok) toast.error('Erreur lors du téléchargement du devis.');
  };

  const handleDownloadCgv = async () => {
    const ok = await downloadCgv({ channel: definition.channel });
    if (!ok) toast.error('Erreur lors du téléchargement des CGV.');
  };

  return {
    actions: {
      previewBat,
      previewingBat,
      downloadDevis: handleDownloadDevis,
      downloadingDevis,
      downloadCgv: handleDownloadCgv,
      downloadingCgv,
    },
    batPreview: {
      ...batPreview,
      onClose: () => setBatPreview(prev => ({ ...prev, open: false })),
    },
  };
};

export type ValidationActions = ReturnType<typeof useValidationActions>['actions'];
